import React from "react";
import PropTypes from "prop-types";
import { injectIntl, FormattedMessage } from "react-intl";

import styles from "./style.module.scss";

/**
 * TextArea Component.
 *
 * A functional React component that renders a text area element.
 * This component allows users to input and edit text, and supports several customization options such as
 * a heading, description, placeholder text, and the number of visible text rows.
 * The text entered by the user can be tracked and stored in a state/store as required.
 */
class BaseTextArea extends React.Component {
  /**
   * handleTextChange - Event handler for the 'onChange' event of the textarea.
   * It dispatches an action to update the text in the store.
   *
   * @param {Object} e - The event object.
   */
  handleTextChange = (e) => {
    this.props.updateText({ [this.props.id]: e.target.value });
  };

  render() {
    const { intl } = this.props;
    let text = "";
    if (this.props.text) {
      text = this.props.text;
    } else if (this.props.defaultText) {
      text = intl.formatMessage({
        id: this.props.defaultText,
        defaultMessage: this.props.defaultText,
        description: "",
      });
    }
    return (
      <div dir="auto">
        {/* Display the heading above the textarea */}
        <h5 className={styles.heading}>
          {this.props.heading && (
            <FormattedMessage
              id={this.props.heading}
              defaultMessage={this.props.heading}
              description=""
            />
          )}
        </h5>

        {/* Display additional description, if provided */}
        {this.props.description && (
          <p className={styles.description}>
            <FormattedMessage
              id={this.props.description}
              defaultMessage={this.props.description}
              description=""
            />
          </p>
        )}

        {/* The main text area for user input */}
        <textarea
          className={styles.textArea}
          rows={this.props.rows}
          onChange={this.handleTextChange}
          dir={intl.locale == "ar" ? "rtl" : "ltr"}
          defaultValue={text}
          {...(this.props.placeholder && {
            placeholder: intl.formatMessage({
              id: this.props.placeholder,
              defaultMessage: this.props.placeholder,
              description: "",
            }),
          })}
        />
      </div>
    );
  }
}

// Specifies the types and structures of props expected by the component
BaseTextArea.propTypes = {
  id: PropTypes.number.isRequired, // Unique identifier for the textarea
  heading: PropTypes.string.isRequired, // Heading text displayed above the textarea
  description: PropTypes.string, // Optional description or instructions
  placeholder: PropTypes.string, // Placeholder text for the textarea when it's empty
  rows: PropTypes.number, // Number of visible rows in the textarea
  text: PropTypes.string, // Text if updated by user
  defaultText: PropTypes.string, // Default or initial text to display in the textarea
};

// Default values for props that are not explicitly provided
BaseTextArea.defaultProps = {
  rows: 5, // Default to showing 5 rows
};

export const TextArea = injectIntl(BaseTextArea);
